import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import api from "@/lib/api";
import { AuthContext } from "@/App";
import { toast } from "sonner";
import { User, Mail, Phone, Shield, Save, Loader2, Camera, FileText, X } from "lucide-react";

export default function Profile() {
  const { user, login } = useContext(AuthContext);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    bio: "",
    profile_image: ""
  });

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    setForm({
      name: user.name || "",
      email: user.email || "",
      mobile: user.mobile || "",
      bio: user.bio || "",
      profile_image: user.profile_image || ""
    });
  }, [user, navigate]);

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'admin': return 'bg-red-600';
      case 'seller': return 'bg-blue-600';
      case 'consultant': return 'bg-purple-600';
      case 'buyer': return 'bg-emerald-600';
      default: return 'bg-slate-600';
    }
  };

  const handleImageClick = () => {
    if (editing) {
      fileInputRef.current?.click();
    }
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB");
      return;
    }

    setUploadingImage(true);
    try {
      // Create a preview URL for the image
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm({ ...form, profile_image: reader.result });
      };
      reader.readAsDataURL(file);
      
      toast.success("Image selected! Save changes to update your profile.");
    } catch (error) {
      toast.error("Failed to process image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setForm({ ...form, profile_image: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { data } = await api.patch("/auth/profile", {
        name: form.name,
        mobile: form.mobile,
        bio: form.bio,
        profile_image: form.profile_image
      });
      
      // Update local storage and context
      const updatedUser = { 
        ...user, 
        name: data.name, 
        mobile: data.mobile,
        bio: data.bio,
        profile_image: data.profile_image
      };
      login(localStorage.getItem("token"), updatedUser);
      
      toast.success("Profile updated successfully!");
      setEditing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm({
      name: user.name || "",
      email: user.email || "",
      mobile: user.mobile || "",
      bio: user.bio || "",
      profile_image: user.profile_image || ""
    });
    setEditing(false);
  };

  if (!user) {
    return null;
  }

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8" data-testid="profile-header">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">My Profile</h1>
          <p className="text-slate-600">View and manage your account information</p>
        </div>

        <Card className="shadow-lg" data-testid="profile-card">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Profile Image */}
                <div className="relative group">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    accept="image/*"
                    className="hidden"
                  />
                  <div 
                    className={`w-20 h-20 rounded-full overflow-hidden border-4 border-white shadow-lg ${editing ? 'cursor-pointer' : ''}`}
                    onClick={handleImageClick}
                  >
                    {form.profile_image ? (
                      <img 
                        src={form.profile_image} 
                        alt={user.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center">
                        <User className="w-10 h-10 text-slate-500" />
                      </div>
                    )}
                  </div>
                  
                  {/* Edit overlay */}
                  {editing && (
                    <div 
                      className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                      onClick={handleImageClick}
                    >
                      {uploadingImage ? (
                        <Loader2 className="w-6 h-6 text-white animate-spin" />
                      ) : (
                        <Camera className="w-6 h-6 text-white" />
                      )}
                    </div>
                  )}
                  
                  {/* Remove image button */}
                  {editing && form.profile_image && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRemoveImage(); }}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 shadow-md"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                
                <div>
                  <CardTitle className="text-2xl">{user.name}</CardTitle>
                  <CardDescription className="flex items-center gap-2 mt-1">
                    <Badge className={getRoleBadgeColor(user.role)}>
                      {user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
                    </Badge>
                  </CardDescription>
                </div>
              </div>
              {!editing && (
                <Button 
                  variant="outline" 
                  onClick={() => setEditing(true)}
                  data-testid="edit-profile-button"
                >
                  Edit Profile
                </Button>
              )}
            </div>
            
            {/* Image upload hint when editing */}
            {editing && (
              <p className="text-xs text-slate-500 mt-3">
                Click on the profile picture to upload a new image (max 5MB)
              </p>
            )}
          </CardHeader>
          
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div className="space-y-2">
                <Label htmlFor="name" className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-500" />
                  Full Name
                </Label>
                {editing ? (
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Enter your name"
                    required
                    data-testid="name-input"
                  />
                ) : (
                  <p className="text-lg text-slate-900 py-2 px-3 bg-slate-50 rounded-md" data-testid="name-display">
                    {user.name}
                  </p>
                )}
              </div>

              {/* Email Field - Read Only */}
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-500" />
                  Email Address
                  <span className="text-xs text-slate-400">(cannot be changed)</span>
                </Label>
                <p className="text-lg text-slate-900 py-2 px-3 bg-slate-100 rounded-md" data-testid="email-display">
                  {user.email}
                </p>
              </div>

              {/* Mobile Field */}
              <div className="space-y-2">
                <Label htmlFor="mobile" className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-slate-500" />
                  Mobile Number
                </Label>
                {editing ? (
                  <Input
                    id="mobile"
                    type="tel"
                    value={form.mobile}
                    onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                    placeholder="Enter your mobile number"
                    data-testid="mobile-input"
                  />
                ) : (
                  <p className="text-lg text-slate-900 py-2 px-3 bg-slate-50 rounded-md" data-testid="mobile-display">
                    {user.mobile || "Not provided"}
                  </p>
                )}
              </div>

              {/* Bio Field */}
              <div className="space-y-2">
                <Label htmlFor="bio" className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-slate-500" />
                  Bio
                  <span className="text-xs text-slate-400">(Tell us about yourself)</span>
                </Label>
                {editing ? (
                  <Textarea
                    id="bio"
                    value={form.bio}
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    placeholder="Write a short bio about yourself, your experience, expertise, or what you're looking for on ReBusi..."
                    rows={4}
                    className="resize-none"
                    data-testid="bio-input"
                  />
                ) : (
                  <div className="py-2 px-3 bg-slate-50 rounded-md min-h-[80px]" data-testid="bio-display">
                    {user.bio ? (
                      <p className="text-slate-900 whitespace-pre-wrap">{user.bio}</p>
                    ) : (
                      <p className="text-slate-400 italic">No bio added yet. Click "Edit Profile" to add one.</p>
                    )}
                  </div>
                )}
                {editing && (
                  <p className="text-xs text-slate-500">
                    {form.bio?.length || 0}/500 characters
                  </p>
                )}
              </div>

              {/* Role Field - Read Only */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-slate-500" />
                  Account Role
                </Label>
                <div className="py-2 px-3 bg-slate-100 rounded-md">
                  <Badge className={`${getRoleBadgeColor(user.role)} text-sm`}>
                    {user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
                  </Badge>
                </div>
              </div>

              {/* Action Buttons */}
              {editing && (
                <div className="flex gap-4 pt-4 border-t border-slate-200">
                  <Button 
                    type="submit" 
                    className="bg-blue-600 hover:bg-blue-700 flex-1"
                    disabled={loading}
                    data-testid="save-profile-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={handleCancel}
                    disabled={loading}
                    data-testid="cancel-edit-button"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </form>
          </CardContent>
        </Card>

        {/* Account Info Card */}
        <Card className="mt-6 shadow-sm" data-testid="account-info-card">
          <CardHeader>
            <CardTitle className="text-lg">Account Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-sm text-slate-600 space-y-2">
              <p>Account ID: <span className="font-mono text-slate-900">{user.id?.slice(0, 8)}...</span></p>
              <p>Member since: <span className="text-slate-900">{user.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}</span></p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
